import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AsyncSubject } from 'rxjs';
import { MEBasicAPIResponse, ResponseCode } from '../api/me-api-response';

/**
 * User session lifecycle (logoff now; login to move here later).
 * Does not depend on UserService (avoids MEAPIService ↔ UserService cycle).
 * Uses HttpClient so XsrfInterceptor still attaches X-XSRF-TOKEN.
 */
@Injectable({
  providedIn: 'root'
})
export class UserSessionService {

  /** Keep in sync with MEAPIService.BASE_URL + '/customer/logoff' (no MEAPIService import — avoids cycle). */
  public static readonly API_LOGOFF: string = '/api/v1/customer/logoff';

  constructor(
    private http: HttpClient,
    private router: Router,
  ) { }

  /**
   * Server logoff. Navigates only when body status is SUCCESS (matches prior SuccessLogoffHandler).
   * Non-SUCCESS / HTTP error: complete without navigate (AllCodeResponseHandler did not navigate).
   */
  public logoff(isSilent: boolean = false, reload: boolean = true, redirectUrl?: string): AsyncSubject<boolean> {
    const result = new AsyncSubject<boolean>();
    this.http.get<MEBasicAPIResponse>(UserSessionService.API_LOGOFF).subscribe({
      next: (body) => {
        if (body?.status?.code === ResponseCode.SUCCESS) {
          this.finishLogout(isSilent, reload, redirectUrl);
          result.next(true);
        } else {
          result.next(false);
        }
        result.complete();
      },
      error: () => {
        result.next(false);
        result.complete();
      }
    });
    return result;
  }

  /**
   * Account Monitoring suspend (4781): always call server logoff then hard return to login.
   */
  public forceLogoff(): void {
    this.http.get<MEBasicAPIResponse>(UserSessionService.API_LOGOFF).subscribe({
      next: () => this.finishLogout(false, true, '/login'),
      error: () => this.finishLogout(false, true, '/login'),
    });
  }

  public finishLogout(isSilent: boolean, reload: boolean, redirectUrl?: string): void {
    if (!isSilent) {
      const target = redirectUrl ?? '/login';
      this.router.navigateByUrl(target).then(() => {
        if (reload) {
          window.location.reload();
        }
      });
    }
  }
}
